import React, { useState, useMemo, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { useAsync } from 'react-async';
import { useDebounce } from 'react-use';
import { isEqual } from 'lodash';
import {
  BenefitKindEnum,
  formatCurrency,
  ProductTypeEnum,
  PromotionKindEnum,
} from 'utils';
import { useKoobStore } from '../../../store';
import { useSnack } from '../../../hooks';
import KoobTabs from '../../../components/Koob/KoobTabs';
import KoobLoaderFullscreen from '@koob/ui/src/components/KoobLoaderFullscreen';
import { KoobCard, KoobPlaceholder, KoobPagination } from '@koob/ui';
import { distributePromotions, getPromotions } from 'api/node/promotions';
import { getPromotionAppliesTo } from 'utils/promotions';

// Reusable Partials
import PromotionCard from './Partials/PromotionCard';
import PromotionsFilterBar, {
  EXPERIENCE_HIDDEN_TYPE_FILTERS,
} from './Partials/PromotionsFilterBar';
import PromotionsBottomBar from './Partials/PromotionsBottomBar';
import BulkEditOrganizationSelector from './Partials/BulkEditOrganizationSelector';
import { OrderDirectionEnum } from '@koob/enums';
import { matchesSearchWords, toSearchWords } from '@koob/text-search';

// Reverse lookup: backend benefit kind (e.g. "Benefits::Percent") -> slug.
const BENEFIT_SLUG_BY_KIND = Object.fromEntries(
  Object.entries(BenefitKindEnum).map(([slug, value]) => [value, slug]),
);

// Normalized "type" slug used for the type filter and the card icon.
const getTypeSlug = (benefitKind) => {
  const slug = BENEFIT_SLUG_BY_KIND[benefitKind];
  if (slug === PromotionKindEnum.SUPPLEMENT_PERCENT)
    return PromotionKindEnum.SUPPLEMENT;
  return slug ?? null;
};

// Format a single numeric benefit value (sign + unit) by benefit kind.
const formatBenefitValue = (kind, value, currency, t) => {
  switch (kind) {
    case BenefitKindEnum.percent:
      return `-${value}%`;
    case BenefitKindEnum.supplement_percent:
      return `+${value}%`;
    case BenefitKindEnum.amount:
      return `-${formatCurrency({ amount: value, currency })}`;
    case BenefitKindEnum.supplement:
      return `+${formatCurrency({ amount: value, currency })}`;
    case BenefitKindEnum.free_night:
      return `${value} ${t('promotions.card.freeNight', { count: value })}`;
    default:
      return null;
  }
};

// Human readable discount derived from the promotion benefit.
const formatDiscount = (promo, t) => {
  const benefit = promo?.benefit;
  const kind = benefit?.kind;
  const currency = benefit?.currency?.toUpperCase();

  // Kinds that don't depend on a numeric value.
  if (kind === BenefitKindEnum.upgrade_room) {
    const fromRoom = benefit?.upgradedFromRoom?.displayName;
    const toRoom = benefit?.upgradedToRoom?.displayName;
    if (fromRoom && toRoom) {
      return `${fromRoom} → ${toRoom}`;
    }
    return t('promotions.card.upgradeRoom');
  }
  if (kind === BenefitKindEnum.amenity) {
    const total = (benefit?.benefitAmenities ?? []).reduce(
      (acc, cur) => acc + (cur?.price ?? 0),
      0,
    );
    return formatCurrency({ amount: total, currency });
  }

  // When the promotion is not per-room, benefit.value is null and the amounts
  // are defined per age range in benefitAgeGroups. Show the value range.
  if (benefit?.value == null && benefit?.benefitAgeGroups?.length > 0) {
    const values = benefit.benefitAgeGroups
      .map((group) => group?.value)
      .filter((value) => value != null);

    if (values.length > 0) {
      const min = formatBenefitValue(kind, Math.min(...values), currency, t);
      const max = formatBenefitValue(kind, Math.max(...values), currency, t);
      if (min && max) {
        return min === max ? min : `${min} → ${max}`;
      }
    }
  }

  return (
    formatBenefitValue(kind, benefit?.value, currency, t) ??
    t('promotions.card.specialOffer')
  );
};

// Map a raw promotion (from the listing endpoint) into the shape the card needs.
const normalizePromotion = (promo, t) => ({
  id: promo?.id,
  name: promo?.name ?? '',
  discount: formatDiscount(promo, t),
  isExperience: promo?.isExperience,
  productName: promo?.productName,
  state: promo?.state,
  kind: promo?.kind ?? null,
  typeSlug: getTypeSlug(promo?.benefit?.kind),
  // Exact benefit slug (supplement_percent is NOT collapsed into supplement
  // here, unlike typeSlug): matches the type filter values one to one, the
  // same way the server maps them in PROMOTION_TYPE_BENEFIT_KINDS.
  benefitSlug: BENEFIT_SLUG_BY_KIND[promo?.benefit?.kind] ?? null,
  appliesTo: getPromotionAppliesTo(promo ?? {}),
});

export default function ToConnectionRequestPromotions() {
  const { t } = useTranslation(['toConnectionRequest', 'misc']);
  const { notify } = useSnack();

  const { clearToConnectionRequestModel } = useKoobStore(
    useShallow((state) => ({
      clearToConnectionRequestModel: state.clearToConnectionRequestModel,
    })),
  );

  // Filters / selection state
  const [activeTab, setActiveTab] = useState(ProductTypeEnum.HOTEL); // hotel | experience
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState(null); // null | free_night | percent | amount | supplement
  const [selectionFilter, setSelectionFilter] = useState('all'); // all | selected | unselected

  // Selections are tracked per tab so the hotel and experience counts stay
  // independent; distribution still submits the union of both tabs. Full promo
  // objects are stored (not just ids) so the "selected" view can render and
  // paginate the whole selection, including promos picked on other pages.
  const [selectedPromosByTab, setSelectedPromosByTab] = useState({
    hotel: [],
    experience: [],
  });
  const selectedPromos = selectedPromosByTab[activeTab];
  // Restrict the working selection to the active type filter (exact benefit
  // slug, mirroring the server-side type filter) so the badge count and the
  // "unselected" exclusion stay consistent with the type-filtered listing.
  const selectedPromoIds = useMemo(
    () =>
      selectedPromos
        .filter((promo) => !typeFilter || promo.benefitSlug === typeFilter)
        .map((promo) => promo.id),
    [selectedPromos, typeFilter],
  );
  const allSelectedPromoIds = useMemo(
    () =>
      [...selectedPromosByTab.hotel, ...selectedPromosByTab.experience].map(
        (promo) => promo.id,
      ),
    [selectedPromosByTab],
  );

  // Debounce the search input so we don't refetch on every keystroke.
  useDebounce(() => setDebouncedSearch(searchQuery), 500, [searchQuery]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSelectedView = selectionFilter === 'selected';
  const isUnselectedView = selectionFilter === 'unselected';

  // In the "unselected" view the selected ids are excluded server-side, so
  // pages come back full and the totals reflect the actual unselected promos.
  const exceptIds = isUnselectedView ? selectedPromoIds : [];

  // Fetch the operational promotions available for distribution to TOs: every
  // offer, a DMC one becoming "DMC & TOs" once distributed. The tab
  // (kind), search, type and exclusion filters are all applied server-side;
  // the "selected" view renders the local selection instead.
  const { data, isLoading } = useAsync({
    promiseFn: getPromotions,
    state: 'operational',
    kind: activeTab,
    search: debouncedSearch,
    type: typeFilter ?? '',
    exceptIds,
    column: undefined,
    direction: OrderDirectionEnum.DESC,
    page,
    perPage,
    watchFn: (props, prevProps) =>
      !isEqual(props.state, prevProps.state) ||
      !isEqual(props.kind, prevProps.kind) ||
      !isEqual(props.search, prevProps.search) ||
      !isEqual(props.type, prevProps.type) ||
      !isEqual(props.exceptIds, prevProps.exceptIds) ||
      !isEqual(props.page, prevProps.page) ||
      !isEqual(props.perPage, prevProps.perPage),
  });

  const promotions = useMemo(
    () => (data?.data ?? []).map((promo) => normalizePromotion(promo, t)),
    [data, t],
  );

  // The "selected" view renders the locally tracked selection, which the
  // server never sees; mirror the server filters client-side (type = exact
  // benefit slug, search = name or KOOB id, KD-3530). Server pages are shown
  // as they come.
  const visibleSelectedPromos = useMemo(() => {
    if (!isSelectedView) return selectedPromos;
    const searchWords = toSearchWords(debouncedSearch);
    return selectedPromos.filter(
      (promo) =>
        (!typeFilter || promo.benefitSlug === typeFilter) &&
        matchesSearchWords(searchWords, [promo.name], promo.id),
    );
  }, [isSelectedView, selectedPromos, typeFilter, debouncedSearch]);

  // "all" and "unselected" render the server page; "selected" renders the
  // local selection, paginated client-side so the paginator count matches the
  // number of selected promos across all pages. In the "unselected" view the
  // server already excludes the selected ids; the local mask only hides
  // promos selected since the last fetch resolved.
  const filteredPromotions = useMemo(() => {
    if (isSelectedView) {
      return visibleSelectedPromos.slice((page - 1) * perPage, page * perPage);
    }
    if (isUnselectedView) {
      return promotions.filter((promo) => !selectedPromoIds.includes(promo.id));
    }
    return promotions;
  }, [
    promotions,
    isSelectedView,
    isUnselectedView,
    visibleSelectedPromos,
    selectedPromoIds,
    page,
    perPage,
  ]);

  // Pagination totals come from the server response headers, except in the
  // "selected" view where pagination runs on the local selection.
  const totalCount = Number(data?.headers?.xTotalCount ?? 0);
  const totalPages = Number(data?.headers?.xTotalPages ?? 1);
  const paginationTotalCount = isSelectedView
    ? visibleSelectedPromos.length
    : totalCount;
  const paginationTotalPages = isSelectedView
    ? Math.max(1, Math.ceil(visibleSelectedPromos.length / perPage))
    : totalPages;

  // The badge shows "selected / total"; in the unselected view the server
  // total excludes the selected promos (restricted to the type filter, like
  // exceptIds), so add that same filtered count back.
  const selectionBadgeTotal = isUnselectedView
    ? totalCount + selectedPromoIds.length
    : totalCount;

  // The "unselected" view keeps its stale items visible while the exclusion
  // refetch runs (so ticking a checkbox does not flash the whole list); the
  // loader only takes over when nothing is displayable, e.g. right after
  // selecting every promo of the current page.
  const showListLoader =
    isLoading &&
    !isSelectedView &&
    (!isUnselectedView || filteredPromotions.length === 0);

  // Reset to the first page whenever the query or the selection view changes.
  useEffect(() => {
    setPage(1);
  }, [activeTab, typeFilter, debouncedSearch, perPage, selectionFilter]);

  // Selecting/deselecting can shrink the current view; keep the page in range.
  useEffect(() => {
    if (!isLoading && paginationTotalPages > 0 && page > paginationTotalPages) {
      setPage(paginationTotalPages);
    }
  }, [isLoading, page, paginationTotalPages]);

  // The free_night / amenity / upgrade_room filters don't apply to experiences;
  // clear the selection when it becomes hidden after switching to that tab.
  useEffect(() => {
    if (
      activeTab === ProductTypeEnum.EXPERIENCE &&
      EXPERIENCE_HIDDEN_TYPE_FILTERS.includes(typeFilter)
    ) {
      setTypeFilter(null);
    }
  }, [activeTab, typeFilter]);

  // Reset filters / selection
  const handleReset = () => {
    setActiveTab(ProductTypeEnum.HOTEL);
    setSearchQuery('');
    setTypeFilter(null);
    setSelectionFilter('all');
    setSelectedPromosByTab({ hotel: [], experience: [] });
    notify(t('misc:resetForm'));
  };

  const handleSelectAll = () => {
    setSelectedPromosByTab((prev) => {
      const currentIds = new Set(prev[activeTab].map((promo) => promo.id));
      const additions = filteredPromotions.filter(
        (promo) => !currentIds.has(promo.id),
      );
      return {
        ...prev,
        [activeTab]: [...prev[activeTab], ...additions],
      };
    });
  };

  const handleDeselectAll = () => {
    const filteredIds = filteredPromotions.map((promo) => promo.id);
    setSelectedPromosByTab((prev) => ({
      ...prev,
      [activeTab]: prev[activeTab].filter(
        (promo) => !filteredIds.includes(promo.id),
      ),
    }));
  };

  const toggleSelectPromo = (promo) => {
    setSelectedPromosByTab((prev) => {
      const current = prev[activeTab];
      return {
        ...prev,
        [activeTab]: current.some((item) => item.id === promo.id)
          ? current.filter((item) => item.id !== promo.id)
          : [...current, promo],
      };
    });
  };

  // Submit / distribution
  const handleDistributionSubmit = async (values) => {
    const organizationIds = values?.organizationIds ?? [];

    if (organizationIds.length === 0) {
      notify(t('bulkEdit.requestSelector.selectError'), { type: 'error' });
      return;
    }

    if (allSelectedPromoIds.length === 0) {
      notify(t('promotions.errors.selectPromotion'), { type: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      await distributePromotions({
        organizationIds,
        promotionIds: allSelectedPromoIds,
      });
      notify(t('promotions.messages.distributed'));
    } catch (error) {
      notify(t('misc:error'), { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: ProductTypeEnum.HOTEL, label: t('promotions.tabs.hotel') },
    { id: ProductTypeEnum.EXPERIENCE, label: t('promotions.tabs.experience') },
  ];

  return (
    <div className="max-w-7xl mx-auto min-h-[150vh] px-4 py-8">
      {isSubmitting && (
        <KoobLoaderFullscreen
          opacity="semi"
          title={t('promotions.loader.title')}
          description={t('promotions.loader.description')}
        />
      )}

      <Formik
        initialValues={{
          organizationIds: [],
        }}
        onSubmit={handleDistributionSubmit}
        enableReinitialize
      >
        {({ values }) => {
          const isApplyDisabled =
            (values?.organizationIds?.length ?? 0) === 0 ||
            allSelectedPromoIds.length === 0;

          return (
            <Form className="space-y-6">
              {/* Top Selector: Tour Operators */}
              <BulkEditOrganizationSelector />

              {/* Tabs, search, list & pagination */}
              <KoobCard padding="none">
                <KoobTabs
                  tabs={tabs}
                  currentTab={activeTab}
                  setCurrentTab={setActiveTab}
                />

                <div className="p-5">
                  <div className="flex flex-col space-y-4">
                    <PromotionsFilterBar
                      activeTab={activeTab}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      typeFilter={typeFilter}
                      setTypeFilter={setTypeFilter}
                      selectionFilter={selectionFilter}
                      setSelectionFilter={setSelectionFilter}
                      onSelectAll={handleSelectAll}
                      onDeselectAll={handleDeselectAll}
                      selectedCount={selectedPromoIds.length}
                      totalCount={selectionBadgeTotal}
                      isLoading={isLoading}
                    />

                    <div className="space-y-3 min-h-[400px]">
                      {showListLoader ? (
                        <KoobPlaceholder
                          count={4}
                          className="w-full px-4 py-8"
                        />
                      ) : filteredPromotions.length > 0 ? (
                        filteredPromotions.map((promo) => (
                          <PromotionCard
                            key={promo.id}
                            promo={promo}
                            isSelected={selectedPromoIds.includes(promo.id)}
                            onToggleSelect={toggleSelectPromo}
                          />
                        ))
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 bg-white">
                          <i className="far fa-folder-open text-4xl text-gray-400" />
                          <p className="text-sm mt-2">
                            {t('promotions.empty')}
                          </p>
                        </div>
                      )}
                    </div>

                    {paginationTotalCount > 0 && (
                      <KoobPagination
                        page={page}
                        setPage={setPage}
                        perPage={perPage}
                        setPerPage={setPerPage}
                        totalCount={paginationTotalCount}
                        totalPages={paginationTotalPages}
                      />
                    )}
                  </div>
                </div>
              </KoobCard>

              {/* Bottom Navigation Toolbar Card */}
              <PromotionsBottomBar
                onReset={handleReset}
                isApplyDisabled={isApplyDisabled}
                clearToConnectionRequestModel={clearToConnectionRequestModel}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
